import { useState, useCallback } from 'react';

type FilterStrategy = 'startsWith' | 'includes';

export function useTabCompletion(
    input: string,
    setInput: (value: string) => void,
    allOptions: string[],
    filterStrategy: FilterStrategy = 'startsWith'
) {
    const [matches, setMatches] = useState<string[]>([]);
    const [matchIndex, setMatchIndex] = useState(-1);
    const [isCycling, setIsCycling] = useState(false);
    const [hint, setHint] = useState('');

    // Update hint based on input and options
    // We only show hits for prefix matches to be safe visually
    const updateHint = useCallback((currentInput: string) => {
        if (!currentInput.trim()) {
            setHint('');
            return;
        }

        const lowerInput = currentInput.toLowerCase();
        // Find first option that STARTS with input (regardless of filter strategy)
        // This ensures the visual ghost text makes sense
        const bestMatch = allOptions.find(opt =>
            opt.toLowerCase().startsWith(lowerInput)
        );

        if (bestMatch) {
            // The hint is the part of the match that comes AFTER the input
            // But to preserve case of the match, we might need to be careful.
            // Actually, we usually want to show the FULL specific casing of the match.
            // But we can't change the user's current input casing visually easily.
            // So we just return the suffix.
            // e.g. Input: "cop", Match: "Copper". Suffix: "per".
            // Display: "cop" + "per". 
            setHint(bestMatch.slice(currentInput.length));
        } else {
            setHint('');
        }
    }, [allOptions]);

    // When input changes, checking cycling and hints
    const handleInputChange = useCallback((val: string) => {
        setInput(val);
        updateHint(val);
        if (isCycling) {
            setIsCycling(false);
            setMatches([]);
            setMatchIndex(-1);
        }
    }, [isCycling, setInput, updateHint]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Tab') {
            e.preventDefault();

            let currentMatches = matches;
            let nextIndex = matchIndex;

            if (!isCycling) {
                // Start cycling
                const searchStr = input.toLowerCase();
                // If input is empty, maybe don't autocomplete? Or autocomplete everything?
                // Usually empty input -> no autocomplete.
                if (!searchStr.trim()) return;

                currentMatches = allOptions.filter(opt =>
                    filterStrategy === 'startsWith'
                        ? opt.toLowerCase().startsWith(searchStr)
                        : opt.toLowerCase().includes(searchStr)
                );

                if (currentMatches.length === 0) return;

                // Sort matches alphabetically
                currentMatches.sort((a, b) => a.localeCompare(b));

                setMatches(currentMatches);
                setIsCycling(true);
                nextIndex = 0;
            } else {
                // Cycle
                nextIndex = (matchIndex + 1) % currentMatches.length;
            }

            setMatchIndex(nextIndex);
            const nextMatch = currentMatches[nextIndex] || '';
            setInput(nextMatch);
            setHint(''); // Clear hint when we fully autocomplete
        } else {
            // Reset on any other key press (excluding modifiers)
            if (e.key !== 'Shift' && e.key !== 'Control' && e.key !== 'Alt' && e.key !== 'Meta' && e.key !== 'CapsLock') {
                setIsCycling(false);
                setMatches([]);
                setMatchIndex(-1);
            }
        }
    }, [input, allOptions, filterStrategy, isCycling, matches, matchIndex, setInput]);

    return {
        handleKeyDown,
        handleInputChange,
        hint,
        currentSuggestion: isCycling && matches.length > 0 ? matches[matchIndex] : null,
        matches: isCycling ? matches : []
    };
}
